import React from 'react';
import { useTranslation } from 'react-i18next';
import { useController } from 'react-hook-form';
import useConfirmDialog from '@/components/ConfirmDialogProvider';
import { Button, Box } from '@mui/material';
import { AudioBlock } from '@/declarations/models/blocks/AudioBlock';
import CheckboxInput from '@/components/forms/CheckboxInput';
import { BlockComponent } from '../../lib/declarations/EditorComponentTypes';
import Container from '../../../components/Container';
import TextInput from '../../../components/forms/TextInput';
import { BlockSpecificPage } from '../../lib/declarations/BlockSpecificPage';
import { WidgetBlock as WidgetBlockModel } from '../../../declarations/models/blocks/WidgetBlock';
import { BlockPath, useCurrentBlock } from '../CurrentBlockProvider';
import { BlockModuleType } from '../../../declarations/models/BlockModuleType';
import Styles from '../../../assets/js/Styles';

export const WidgetBlock: BlockComponent = () => {
  const { t } = useTranslation('components');
  const { blockPath } = useCurrentBlock();
  const confirm = useConfirmDialog();

  const {
    field: { onChange: onChangeType },
  } = useController<BlockSpecificPage<WidgetBlockModel>, `${BlockPath}.type`>({
    name: `${blockPath}.type`,
  });

  const {
    field: { onChange: onChangeSource },
  } = useController<BlockSpecificPage<AudioBlock>, `${BlockPath}.source`>({
    name: `${blockPath}.source`,
  });

  const {
    field: { onChange: onChangeLayout },
  } = useController<BlockSpecificPage<AudioBlock>, `${BlockPath}.layout`>({
    name: `${blockPath}.layout`,
  });

  const {
    field: { onChange: onChangeConvertedFromWidget },
  } = useController<BlockSpecificPage<AudioBlock>, `${BlockPath}.convertedFromWidget`>({
    name: `${blockPath}.convertedFromWidget`,
  });

  const convertToAudioWidget = async () => {
    if (await confirm(`${t('WidgetBlock.convertConfirm')}`)) {
      onChangeSource('external');
      onChangeLayout('normal');
      onChangeConvertedFromWidget(true);
      onChangeType('article__audio');
    }
  };
  const convertToVideoWidget = async () => {
    if (await confirm(`${t('WidgetBlock.convertConfirm')}`)) {
      onChangeSource('external');
      onChangeConvertedFromWidget(true);
      onChangeType('article__video');
    }
  };

  return (
    <Container gap={2} column top left fullWidth data-testid='widgetBlock'>
      <Container gap={2} column top left fullWidth>
        <Container gap={1} top left column fullWidth>
          <TextInput<BlockSpecificPage<WidgetBlockModel>>
            path={`${blockPath}.html`}
            label={t('WidgetBlock.html')}
            type='text'
            multiline
            minRows={6}
            maxRows={30}
          />

          <Container gap={2} top left fullWidth>
            <CheckboxInput<BlockSpecificPage<WidgetBlockModel>>
              path={`${blockPath}.isWidget`}
              label={t('WidgetBlock.isWidget')}
            />
          </Container>

          <Container
            column
            fullWidth
            px={2}
            py={1}
            mt={0}
            gap={0}
            left
            sx={{
              backgroundColor: Styles.Colors.LIGHT_BLUE,
              border: `1px solid ${Styles.Colors.MEDIUM_BLUE}`,
            }}>
            <p dangerouslySetInnerHTML={{ __html: t('WidgetBlock.securityInfo') }} />
          </Container>
        </Container>
      </Container>
      <Container gap={1} column top left fullWidth>
        <h3 className='block-sub-label'>{t('WidgetBlock.iframeSettings')}</h3>
        <Container gap={2} top left fullWidth>
          <TextInput<BlockSpecificPage<WidgetBlockModel>>
            path={`${blockPath}.iframeSrc`}
            label={t('WidgetBlock.src')}
          />

          <Box sx={{ width: '30%' }}>
            <TextInput<BlockSpecificPage<WidgetBlockModel>>
              path={`${blockPath}.iframeHeight`}
              label={t('WidgetBlock.height')}
            />
          </Box>
        </Container>

        <h3 className='block-sub-label'>{t('WidgetBlock.additionalSettings')}</h3>
        <TextInput<BlockSpecificPage<WidgetBlockModel>>
          path={`${blockPath}.externalUrl`}
          label={t('WidgetBlock.externalUrl')}
        />

        <Container
          column
          fullWidth
          p={2}
          mt={2}
          gap={0}
          left
          sx={{
            backgroundColor: Styles.Colors.LIGHT_BLUE,
            border: `1px solid ${Styles.Colors.MEDIUM_BLUE}`,
          }}>
          <h3 className='box-label'>{t('WidgetBlock.convert')}</h3>
          <p>{t('WidgetBlock.convertInfo')} </p>

          <Container gap={2} top left fullWidth mt={2}>
            <Button color='secondary' variant='contained' onClick={convertToAudioWidget}>
              {t('WidgetBlock.convertToAudio')}
            </Button>

            <Button color='secondary' variant='contained' onClick={convertToVideoWidget}>
              {t('WidgetBlock.convertToVideo')}
            </Button>
          </Container>
        </Container>
      </Container>
    </Container>
  );
};

export default WidgetBlock;
